import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faSpinner,
  faExclamationTriangle,
  faTrophy,
  faUsers,
  faMapMarked
} from '@fortawesome/free-solid-svg-icons';
import { baseURL, CURRENT_SEASON, LATEST_SEASON } from '../../Constants';
import { AuthContext } from '../../context/AuthContext';
import '../../styles/admin/admin-match-details.scss';

// Define interfaces
interface Team {
  id: string;
  name: string;
  logo: string | null;
  division?: string | null;
  seed?: string | null;
  players: any[];
}

interface Match {
  vetoLog?: string | null;
  gameId: string;
  matchId: string;
  mapName: string;
  season: number;
  division: string;
  team1: Team;
  team2: Team;
  team1PlayerIds: string[];
  team2PlayerIds: string[];
  team1Score: number;
  team2Score: number;
  team1Won: boolean;
  bo3: boolean;
  playoff: boolean;
  bracketPosition?: number | null;
  matchTime: string;
  roundEnds: Record<string, any>;
}

interface Account {
  steamID: string;
  playerName: string;
  name?: string;
  role?: string;
}

const ViewMatchDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { matchId } = useParams<{ matchId: string }>();
  const { token, isAdmin } = useContext(AuthContext);
  const teamPlaceholderImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23333'/%3E%3Ctext x='50' y='50' font-size='18' text-anchor='middle' alignment-baseline='middle' font-family='sans-serif' fill='%23fff'%3ENo Logo%3C/text%3E%3C/svg%3E";
  const [match, setMatch] = useState<Match | null>(null);
  const [team1Players, setTeam1Players] = useState<Account[]>([]);
  const [team2Players, setTeam2Players] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Check if user is an admin before allowing access
  useEffect(() => {
    if (!token || !isAdmin) {
      navigate('/admin/login');
    }
  }, [token, isAdmin, navigate]);
  
  // Fetch match data and players
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // First get all matches for the season
        // Since we don't have a direct endpoint to get a single match by ID,
        // we'll get all matches and filter the one we need
        const seasonsResponse = await fetch(`${baseURL}/Match/getAllMatches`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(2) // Start with season 1, we'll check others if needed
        });
        
        if (!seasonsResponse.ok) {
          throw new Error(`Error fetching matches: ${seasonsResponse.status}`);
        }
        
        const seasonsData = await seasonsResponse.json();
        let matchData = seasonsData.playedMatches.find((m: Match) => m.matchId === matchId);
        
        // If not found in season 1, try other seasons
        if (!matchData) {
          for (let season = CURRENT_SEASON; season <= LATEST_SEASON + 8; season++) { // Try future seasons
            const nextSeasonResponse = await fetch(`${baseURL}/Match/getAllMatches`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify(season)
            });
            
            if (nextSeasonResponse.ok) {
              const nextSeasonData = await nextSeasonResponse.json();
              matchData = nextSeasonData.playedMatches.find((m: Match) => m.matchId === matchId);
              
              if (matchData) {
                break;
              }
            }
          }
        }
        
        if (!matchData) {
          throw new Error('Match not found');
        }
        
        setMatch(matchData);
        
        // Fetch player information for both teams
        const playersData: Account[] = [];
        
        for (const steamId of [...matchData.team1PlayerIds, ...matchData.team2PlayerIds]) {
          try {
            const playerResponse = await fetch(`${baseURL}/Account/${steamId}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            if (playerResponse.ok) {
              const playerData = await playerResponse.json();
              playersData.push(playerData);
            }
          } catch (err) {
            console.warn(`Failed to fetch player data for ${steamId}`);
          }
        }
        
        // Separate players by team
        const team1PlayersData = playersData.filter(p => 
          matchData.team1PlayerIds.includes(p.steamID)
        );
        
        const team2PlayersData = playersData.filter(p => 
          matchData.team2PlayerIds.includes(p.steamID)
        );
        
        setTeam1Players(team1PlayersData);
        setTeam2Players(team2PlayersData);
      } catch (err) {
        console.error('Error loading match data:', err);
        setError('Failed to load match details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
    
    // Set page title
    document.title = 'Match Details - Northern Championships Series Admin';
  }, [matchId, token]);
  
  // Format date and time
  const formatDateTime = (dateTimeString: string) => {
    const date = new Date(dateTimeString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  if (loading) {
    return (
      <div className="admin-match-details loading">
        <div className="container">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }
  
  if (!match) {
    return (
      <div className="admin-match-details">
        <div className="container">
          <div className="editor-header">
            <button 
              type="button"
              className="back-button"
              onClick={() => navigate('/admin/tournaments')}
            >
              <FontAwesomeIcon icon={faArrowLeft} /> Back to Tournaments
            </button>
            <h1>Match Details</h1>
          </div>
          
          <div className="alert alert-danger">
            <FontAwesomeIcon icon={faExclamationTriangle} /> 
            {error || 'Match not found'}
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="admin-match-details">
      <div className="container">
        <div className="editor-header">
          <button 
            type="button"
            className="back-button"
            onClick={() => navigate('/admin/tournaments')}
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Back to Tournaments
          </button>
          <h1>Match Details</h1>
        </div>
        
        {error && (
          <div className="alert alert-danger">
            <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
          </div>
        )}
        
        <div className="match-header">
          <div className="match-header-top">
            <div className="match-date">
              {formatDateTime(match.matchTime)}
            </div>
            <div className="match-info">
              <span className="match-season">Season {match.season}</span>
              <span className="match-division">{match.division}</span>
              <span className="match-type">
                {match.playoff ? 'Playoff' : 'Regular'}
                {match.bo3 ? ' (BO3)' : ''}
              </span>
            </div>
          </div>
          
          <div className="match-teams">
            <div className={`team ${match.team1Won ? 'winner' : ''}`}>
              <div className="team-logo">
                <img 
                  src={match.team1.logo ? `${baseURL}/Team/${match.team1.id}/logo` : teamPlaceholderImage} 
                  alt={`${match.team1.name} logo`}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = teamPlaceholderImage;
                  }}
                />
              </div>
              <div className="team-name">{match.team1.name}</div>
            </div>
            
            <div className="match-score">
              <div className="score-container">
                <span className={match.team1Won ? 'winner' : ''}>{match.team1Score}</span>
                <span className="score-separator">:</span>
                <span className={!match.team1Won ? 'winner' : ''}>{match.team2Score}</span>
              </div>
              <div className="match-map">
                <FontAwesomeIcon icon={faMapMarked} /> {match.mapName}
              </div>
            </div>
            
            <div className={`team ${!match.team1Won ? 'winner' : ''}`}>
              <div className="team-logo">
                <img 
                  src={match.team2.logo ? `${baseURL}/Team/${match.team2.id}/logo` : teamPlaceholderImage} 
                  alt={`${match.team2.name} logo`}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = teamPlaceholderImage;
                  }}
                />
              </div>
              <div className="team-name">{match.team2.name}</div>
            </div>
          </div>
        </div>
        
        <div className="match-details-grid">
          <div className="match-players-section">
            <h2><FontAwesomeIcon icon={faUsers} /> Players</h2>
            
            <div className="players-grid">
              <div className="team-players">
                <h3>{match.team1.name} Players</h3>
                
                {team1Players.length === 0 ? (
                  <p className="no-players">No player data available</p>
                ) : (
                  <div className="players-list">
                    {team1Players.map(player => (
                      <div key={player.steamID} className="player-card">
                        <div className="player-avatar">
                          <img 
                            src={`${baseURL}/Account/${player.steamID}/avatar?t=${new Date().getTime()}`} 
                            alt={`${player.playerName}'s avatar`}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = teamPlaceholderImage;
                            }}
                          />
                        </div>
                        <div className="player-info">
                          <div className="player-name">{player.playerName}</div>
                          {player.name && <div className="player-real-name">{player.name}</div>}
                          {player.role && <div className="player-role">{player.role}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="team-players">
                <h3>{match.team2.name} Players</h3>
                
                {team2Players.length === 0 ? (
                  <p className="no-players">No player data available</p>
                ) : (
                  <div className="players-list">
                    {team2Players.map(player => (
                      <div key={player.steamID} className="player-card">
                        <div className="player-avatar">
                          <img 
                            src={`${baseURL}/Account/${player.steamID}/avatar?t=${new Date().getTime()}`} 
                            alt={`${player.playerName}'s avatar`}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = teamPlaceholderImage;
                            }}
                          />
                        </div>
                        <div className="player-info">
                          <div className="player-name">{player.playerName}</div>
                          {player.name && <div className="player-real-name">{player.name}</div>}
                          {player.role && <div className="player-role">{player.role}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="match-meta-section">
            <h2><FontAwesomeIcon icon={faTrophy} /> Match Information</h2>
            
            <div className="match-meta-grid">
              <div className="meta-item">
                <div className="meta-label">Game ID</div>
                <div className="meta-value">{match.gameId}</div>
              </div>
              
              <div className="meta-item">
                <div className="meta-label">Match ID</div>
                <div className="meta-value">{match.matchId}</div>
              </div>
              
              <div className="meta-item">
                <div className="meta-label">Map</div>
                <div className="meta-value">{match.mapName}</div>
              </div>
              
              <div className="meta-item">
                <div className="meta-label">Season</div>
                <div className="meta-value">{match.season}</div>
              </div>
              
              <div className="meta-item">
                <div className="meta-label">Division</div>
                <div className="meta-value">{match.division}</div>
              </div>
              
              <div className="meta-item">
                <div className="meta-label">Match Type</div>
                <div className="meta-value">
                  {match.playoff ? 'Playoff' : 'Regular'}
                  {match.bo3 ? ' (Best of 3)' : ''}
                </div>
              </div>
              
              {match.bracketPosition && (
                <div className="meta-item">
                  <div className="meta-label">Bracket Position</div>
                  <div className="meta-value">{match.bracketPosition}</div>
                </div>
              )}
            </div>
            
            {match.vetoLog && (
              <div className="veto-section">
                <h3>Map Veto Process</h3>
                <div className="veto-content">
                  <pre>{match.vetoLog}</pre>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewMatchDetailsPage;